import type { ReactNode } from "react";
import { Image, Text, View } from "react-native";
import { SvgXml } from "react-native-svg";

import { useColorScheme } from "@/theme/colorScheme";
import {
  SLAB_COLORS,
  type SlabColors,
  type SlabDensity,
  slabDensity,
  slabParts,
  slabStrip,
  slabStyle,
} from "./slab";
import { Wash } from "./Wash";

/**
 * A graded card in its grader's holder (TRO-627).
 *
 * A dealer recognizes a slab across a table by the holder's silhouette and the label at its top,
 * not by a text badge. This draws the case, the grader's own mark on a white label plate, the grade
 * beside it, and the card in the window below.
 *
 * It wraps the art rather than rendering it. `children` land in the card window, so the caller
 * keeps its own `CardArt` (its `contentFit`, its no-image fallback) and any overlay there, such as
 * `CardTile`'s foil shimmer, which must cross the card and not the case. `slabCardWindow` publishes
 * that box so a caller can measure against it, as `cardTileArtBox` does for the tile.
 *
 * It is drawn as the object it depicts: glossy acrylic with a bevelled lip, catching one diagonal
 * highlight and casting a soft shadow. That is the named exception DESIGN.md §7 carries for this
 * component, and it is the whole of it — the exception covers a depicted holder and nothing else,
 * so no panel, row, card or button inherits a gradient or a shadow from here (TRO-1234).
 *
 * The depth is drawn with `react-native-svg`, which the app already carries, and RN's own shadow
 * and elevation. Nothing here needs a new native module, so a change to this file still reaches
 * installed builds over the air.
 *
 * The density ladder (`slabDensity`, see `slab.ts`) is keyed on the measured width, not on the
 * calling screen. At `seal` this returns the art untouched, because the caller's own chip is the
 * right artifact at thumbnail size. The depth follows that ladder rather than applying flat:
 * twenty slabs in an inventory grid are not four in a carousel, so only `full` casts a shadow and
 * takes the gloss. `compact` keeps the plate's bevel, which is what makes the strip read as a
 * label rather than a sticker, and nothing else.
 */
export function SlabFrame(
  { grader, grade, width, height, density: forced, children }: {
    /** The grading company as recorded, in any case. `null` is a slab with no grader on file. */
    grader: string | null | undefined;
    /** The grade as recorded ("10", "9.5", "GEM MT 10"), printed as given and never reformatted. */
    grade: string | null | undefined;
    /** The art box this slab is laid out inside, in px. */
    width: number;
    height: number;
    /** Overrides the width-derived density, for previews and tests only. */
    density?: SlabDensity;
    children: ReactNode;
  },
) {
  const { colorScheme } = useColorScheme();
  const c = SLAB_COLORS[colorScheme === "dark" ? "dark" : "light"];
  const style = slabStyle(grader);
  const density = forced ?? slabDensity(width);

  if (density === "seal") return <>{children}</>;

  // One announcement for the whole plate, in the order the app reads a seal elsewhere ("PSA 10").
  // The mark is an image, not text, so without this a screen reader would hear only a bare "10".
  const spoken = [style.key, grade].filter(Boolean).join(" ") || "Graded";

  if (density === "compact") {
    const strip = slabStrip(width, height);
    return (
      <>
        {children}
        <View
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: strip.width,
            height: strip.height,
          }}
        >
          <Plate
            width={strip.width}
            height={strip.height}
            style={style}
            grade={grade}
            colors={c}
            spoken={spoken}
          />
        </View>
      </>
    );
  }

  const { slab, plate, card } = slabParts(width, height);
  // A holder's corners are barely rounded; scaling the radius with the slab keeps them that way at
  // every size.
  const radius = Math.max(2, slab.width * 0.035);
  return (
    <>
      {/* The case: acrylic over the art box's ground, lit from above and casting below. */}
      <View
        style={{
          position: "absolute",
          top: slab.top,
          left: slab.left,
          width: slab.width,
          height: slab.height,
          backgroundColor: c.case,
          borderColor: c.edge,
          borderWidth: 1,
          borderRadius: radius,
          shadowColor: c.cast,
          shadowOpacity: 1,
          shadowRadius: Math.max(3, slab.width * 0.09),
          shadowOffset: { width: 0, height: Math.max(1, slab.width * 0.035) },
          // Android draws from `elevation` alone and needs the opaque background above it, which
          // the case already has. iOS reads the four shadow props and ignores this.
          elevation: 4,
        }}
      />
      <View
        style={{
          position: "absolute",
          top: plate.top,
          left: plate.left,
          width: plate.width,
          height: plate.height,
        }}
      >
        <Plate
          width={plate.width}
          height={plate.height}
          style={style}
          grade={grade}
          colors={c}
          spoken={spoken}
        />
      </View>
      {
        /* The window. `overflow: hidden` clips the caller's fill-sized children to the window's
          shape rather than the art box's. */
      }
      <View
        style={{
          position: "absolute",
          top: card.top,
          left: card.left,
          width: card.width,
          height: card.height,
          overflow: "hidden",
        }}
      >
        {children}
        {
          /* The window is recessed into the case, so its top edge shades the card. Drawn over the
            art rather than behind it, because that is where the case's own lip falls. */
        }
        <Wash
          width={card.width}
          height={Math.max(2, card.height * 0.1)}
          stops={[
            { offset: "0", color: "#000000", opacity: 0.22 },
            { offset: "1", color: "#000000", opacity: 0 },
          ]}
        />
      </View>
      {
        /* The gloss, last so it crosses plate and window alike: one diagonal highlight, the way a
          holder catches a room's light. It takes no touches, so a tap still reaches the caller. */
      }
      <View
        pointerEvents="none"
        style={{
          position: "absolute",
          top: slab.top,
          left: slab.left,
          width: slab.width,
          height: slab.height,
          borderRadius: radius,
          overflow: "hidden",
        }}
      >
        <Wash
          width={slab.width}
          height={slab.height}
          x2="0.8"
          stops={[
            { offset: "0", color: "#ffffff", opacity: 0.5 },
            { offset: "0.34", color: "#ffffff", opacity: 0.14 },
            { offset: "0.56", color: "#ffffff", opacity: 0 },
          ]}
        />
      </View>
    </>
  );
}

/**
 * The label plate: the grader's mark on the left, the grade on the right, and a hairline of the
 * grader's color under both.
 *
 * Marks are matched optically, not by width or height. Equal widths print Beckett's square crest
 * nearly three times taller than PSA's lockup. Equal heights make the crest cover a third of the
 * area a wide lockup does, so it reads as a smudge beside it.
 *
 * So height scales with the square root of how far a mark's aspect sits from a reference lockup,
 * the way a capital O is drawn slightly taller than a capital H. A 2.5:1 wordmark gets the base
 * height, and a squarer mark stands taller, up to the plate's inner height. A width cap applies
 * last, so a very wide lockup cannot crowd out the grade. Vector and raster marks size the same way.
 */
function Plate(
  { width, height, style, grade, colors, spoken }: {
    width: number;
    height: number;
    style: ReturnType<typeof slabStyle>;
    grade: string | null | undefined;
    colors: SlabColors;
    spoken: string;
  },
) {
  const pad = Math.max(3, height * 0.16);
  const inner = height - pad * 2;
  // A typical grader lockup's aspect (PSA, CGC, SGC and TAG sit near it). A mark at this aspect
  // gets exactly the base height, and every other mark is measured against it.
  const REFERENCE_ASPECT = 2.5;
  const aspect = style.logo?.aspect ?? REFERENCE_ASPECT;
  let logoHeight = Math.min(inner, height * 0.52 * Math.sqrt(REFERENCE_ASPECT / aspect));
  let logoWidth = logoHeight * aspect;
  // The grade needs the other end of the plate more than a wide mark needs its last few pixels.
  const maxLogoWidth = width * 0.55;
  if (logoWidth > maxLogoWidth) {
    logoWidth = maxLogoWidth;
    logoHeight = logoWidth / aspect;
  }
  // Below this a mark is texture rather than an identity, and the grader's name reads better.
  const fits = !!style.logo && logoHeight >= 9;
  // Floored at 9px, below which a number stops being legible. The grade is the one thing on the
  // plate that must stay readable at every size the ladder renders.
  const gradeSize = Math.max(9, Math.min(20, height * 0.46));
  return (
    <View
      accessible
      accessibilityLabel={spoken}
      style={{
        flex: 1,
        backgroundColor: colors.plate,
        borderRadius: Math.max(1, height * 0.08),
        // The grader's hairline under their mark. A grader with no sourced color gets none rather
        // than a guessed one.
        borderBottomWidth: style.accent ? Math.max(1, height * 0.07) : 0,
        borderBottomColor: style.accent ?? "transparent",
        overflow: "hidden",
      }}
    >
      {
        /* The plate's bevel: a label plate is card stock under the case's lip, lit at its top edge
          and falling away below. It is what keeps the strip reading as a label rather than a
          sticker, so `compact` keeps it even though it casts nothing. */
      }
      <Wash
        width={width}
        height={height}
        stops={[
          { offset: "0", color: "#ffffff", opacity: 1 },
          { offset: "1", color: colors.bevel, opacity: 1 },
        ]}
      />
      {
        /* The mark and grade sit in their own View, which is not decoration.
          On web a vector mark renders as a bare `<svg>`, which is `position: static`, while the
          wash above is absolutely positioned — and CSS paints positioned boxes over in-flow ones
          whatever their DOM order, so the bevel covered the grader's logo. The grade survived only
          because react-native-web gives `Text` a position of its own, which is what made the bug
          look like "PSA has no logo" rather than "the plate paints over its contents".
          A View carries react-native-web's `position: relative`, so the row paints after the wash
          on web and stacks by order on native, as it always did. */
      }
      <View
        style={{
          flex: 1,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          paddingHorizontal: pad,
        }}
      >
        {fits && style.logo
          ? style.logo.kind === "vector"
            ? <SvgXml xml={style.logo.xml} width={logoWidth} height={logoHeight} />
            : (
              <Image
                source={style.logo.source}
                style={{ width: logoWidth, height: logoHeight }}
                // The mark is already laid out at its own aspect, so `contain` only guards against a
                // registry `aspect` that has drifted from the file, letterboxing instead of stretching.
                resizeMode="contain"
              />
            )
          : (
            <Text
              numberOfLines={1}
              style={{ color: colors.ink, fontSize: Math.max(8, Math.min(15, height * 0.36)) }}
              className="font-label"
            >
              {style.wordmark}
            </Text>
          )}
        {grade
          ? (
            <Text
              numberOfLines={1}
              style={{ color: colors.ink, fontSize: gradeSize }}
              className="font-num-bold"
            >
              {grade}
            </Text>
          )
          : null}
      </View>
    </View>
  );
}
